import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/lib/permissions";
import { Pencil, Trash2, Plus, PackageCheck, IndianRupee, Search, Save, MessageCircle, FileDown } from "lucide-react";
import { PageToolbar, EmptyState } from "@/components/PageToolbar";
import ImportButton from "@/components/ImportButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MobileInput, { isValidMobile } from "@/components/MobileInput";
import { formatBill } from "@/lib/format";
import Pagination, { usePagination } from "@/components/Pagination";

function QuickAddDialog({ open, setOpen, title, nameLabel, onSave }) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setName(""); setMobile(""); } }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!isValidMobile(mobile)) {
      toast.error("Mobile must be exactly 10 digits");
      return;
    }
    setSaving(true);
    try {
      const created = await onSave({ name, mobile });
      toast.success(`${title.replace("Add New ", "")} added`);
      setOpen(false);
      return created;
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md bg-white" data-testid="quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">{nameLabel} <span className="text-red-500">*</span></Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} className="h-10" data-testid="quick-add-name" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Mobile <span className="text-red-500">*</span></Label>
            <MobileInput required value={mobile} onChange={(e) => setMobile(e.target.value)} className="h-10" data-testid="quick-add-mobile" />
          </div>
          <DialogFooter className="col-span-2 pt-2 flex gap-2">
            <Button type="submit" disabled={saving} className="bg-[#2E77E6] hover:bg-[#2361C4] text-white" data-testid="quick-add-save">
              Save {title.replace("Add New ", "")}
            </Button>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="bg-slate-500 hover:bg-slate-600 text-white border-0">Close</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Receipts() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const canEdit   = isAdmin || !!currentUser?.permissions?.["Receipt"]?.edit;
  const canDelete = isAdmin || !!currentUser?.permissions?.["Receipt"]?.delete;
  const canDeliver = usePermission("Receipt", "deliver");
  const canAdd = usePermission("Receipt", "add");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [services, setServices] = useState([]);
  const [ptypes, setPtypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const { page, setPage, pageRows, totalPages, total, pageSize, setPageSize } = usePagination(rows, 50, q);

  const load = async () => {
    const [r, c, a, s, p, u, b] = await Promise.all([
      api.get("/receipts", { params: q ? { q } : {} }),
      api.get("/customers"),
      api.get("/agents"),
      api.get("/services"),
      api.get("/payment-types"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/branches").catch(() => ({ data: [] })),
    ]);
    setRows(r.data); setCustomers(c.data); setAgents(a.data); setServices(s.data);
    setPtypes(p.data); setUsers(u.data); setBranches(b.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const empty = {
    doc_date: new Date().toISOString().slice(0, 10),
    type: "Service",
    application_id: "",
    service_id: "", customer_id: "", agent_id: "",
    payment_type: "",
    amount: "", fee: "", balance: "", advance: "", paid_amount: "",
    completion_date: "",
    cur_500: "", cur_200: "", cur_100: "", cur_50: "",
    remark_1: "", remark_2: "", remark_3: "",
    status: "Pending",
    active: true,
    operator: currentUser?.name || currentUser?.email || "",
    supervisor: "",
    branch_id: "",
    branch_name: "",
  };
  const [form, setForm] = useState(empty);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [addAgentOpen, setAddAgentOpen] = useState(false);

  const quickAddCustomer = async ({ name, mobile }) => {
    const { data } = await api.post("/customers", { account: name, mobile });
    setCustomers((cs) => [data, ...cs]);
    setForm((f) => ({ ...f, customer_id: data.id }));
    return data;
  };

  const quickAddAgent = async ({ name, mobile }) => {
    const { data } = await api.post("/agents", { name, mobile });
    setAgents((as) => [data, ...as]);
    setForm((f) => ({ ...f, agent_id: data.id }));
    return data;
  };

  useEffect(() => {
    if (open) setForm(editing ? { ...empty, ...editing } : empty);
    // eslint-disable-next-line
  }, [open, editing]);

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    // auto-calc balance = amount - (advance + paid_amount) if amount present
    if (["amount", "advance", "paid_amount"].includes(k)) {
      const amt = Number(next.amount || 0);
      const adv = Number(next.advance || 0);
      const paid = Number(next.paid_amount || 0);
      next.balance = Math.max(0, amt - adv - paid);
    }
    // denomination auto-fill paid_amount if user is entering denominations
    if (["cur_500", "cur_200", "cur_100", "cur_50"].includes(k)) {
      const total = Number(next.cur_500 || 0) * 500 + Number(next.cur_200 || 0) * 200 + Number(next.cur_100 || 0) * 100 + Number(next.cur_50 || 0) * 50;
      if (total > 0) next.paid_amount = total;
    }
    return next;
  });

  const save = async (e) => {
    e.preventDefault();
    const cust = customers.find((c) => c.id === form.customer_id);
    const svc = services.find((s) => s.id === form.service_id);
    const ag = agents.find((a) => a.id === form.agent_id);
    const br = branches.find((b) => b.id === form.branch_id);
    const payload = {
      ...form,
      customer_name: cust?.account || form.customer_name || "",
      mobile: cust?.mobile || form.mobile || "",
      service_name: svc?.name || form.service_name || "",
      agent_name: ag?.name || form.agent_name || "",
      branch_name: br?.name || form.branch_name || "",
      amount: Number(form.amount || 0),
      fee: Number(form.fee || 0),
      advance: Number(form.advance || 0),
      paid_amount: Number(form.paid_amount || 0),
      balance: Number(form.balance || 0),
      cur_500: Number(form.cur_500 || 0),
      cur_200: Number(form.cur_200 || 0),
      cur_100: Number(form.cur_100 || 0),
      cur_50: Number(form.cur_50 || 0),
    };
    try {
      let savedRow;
      if (editing) {
        const res = await api.put(`/receipts/${editing.id}`, { ...editing, ...payload });
        savedRow = res.data;
        toast.success("Receipt updated");
      } else {
        const res = await api.post("/receipts", payload);
        savedRow = res.data;
        toast.success("Receipt created");
      }
      setOpen(false);
      await load();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const del = async (r) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/receipts/${r.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const markDelivered = async (r) => {
    try {
      await api.patch(`/receipts/${r.id}/status`, { status: "Delivered" });
      toast.success(`Marked ${formatBill(r.doc_no)} as Delivered`);
      await load();
      // Auto-open WhatsApp share on delivery if mobile present
      if (r.mobile) sendWhatsApp({ ...r, status: "Delivered" });
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const sendWhatsApp = (r) => {
    const number = (r.mobile || "").replace(/\D/g, "");
    if (!number) { toast.error("Customer mobile is missing"); return; }
    const receiptUrl = `${window.location.origin}/receipt/${r.id}`;
    const msg = [
      `*Receipt Slip*`,
      ``,
      `*Receipt No:* ${formatBill(r.doc_no)}`,
      `*Date:* ${r.doc_date}`,
      `*Customer:* ${r.customer_name || "-"}`,
      `*Mobile:* ${r.mobile || "-"}`,
      `*Service:* ${r.service_name || "-"}`,
      ``,
      `View full receipt online: ${receiptUrl}`,
      ``,
      `Thank you for choosing SHREE SAMARTH CONSULTANCY!`,
    ].filter(Boolean).join("\n");
    const url = `https://wa.me/91${number}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const downloadPdf = async (r) => {
    try {
      const res = await api.get(`/receipts/${r.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Receipt-${(formatBill(r.doc_no) || r.id).replace(/[^A-Za-z0-9._-]+/g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Receipt PDF downloaded");
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div data-testid="receipts-page">
      <PageToolbar
        onNew={() => { setEditing(null); setOpen(true); }}
        newLabel="New Service / Receipt"
        exportName={isAdmin ? "receipts" : null}
        searchValue={q}
        onSearchChange={setQ}
        extra={
          <>
            <Button
              type="button"
              onClick={() => setBalanceOpen(true)}
              className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              data-testid="collect-balance-btn"
            >
              <IndianRupee className="h-4 w-4 mr-1.5" /> Collect Balance
            </Button>
            {isAdmin && (
              <ImportButton resource="receipts" onDone={load} label="Import Excel" />
            )}
          </>
        }
      />
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm crm-table">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold">Type</th>
                <th className="px-3 py-2.5 text-left font-semibold">Doc No</th>
                <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                <th className="px-3 py-2.5 text-left font-semibold">Service</th>
                <th className="px-3 py-2.5 text-left font-semibold">Customer</th>
                <th className="px-3 py-2.5 text-left font-semibold">Payment</th>
                <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
                <th className="px-3 py-2.5 text-right font-semibold">Advance</th>
                <th className="px-3 py-2.5 text-right font-semibold">Paid</th>
                <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
                <th className="px-3 py-2.5 text-left font-semibold">Status</th>
                <th className="px-3 py-2.5 text-right font-semibold w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageRows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                      r.type === "Service"
                        ? "bg-[color:var(--brand-teal)]/15 text-[color:var(--brand-teal)]"
                        : "bg-[color:var(--brand-ochre)]/15 text-[color:var(--brand-ochre)]"
                    }`}>{r.type || "Service"}</span>
                  </td>
                  <td className="px-3 py-2 font-mono-num text-slate-800 font-semibold">{formatBill(r.doc_no)}</td>
                  <td className="px-3 py-2 font-mono-num text-slate-600">{r.doc_date}</td>
                  <td className="px-3 py-2">{r.service_name}</td>
                  <td className="px-3 py-2">{r.customer_name}<div className="text-[10px] text-slate-400 font-mono-num">{r.mobile}</div></td>
                  <td className="px-3 py-2 text-slate-600">{r.payment_type}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{inr(r.amount)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{inr(r.advance)}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold">{inr(r.paid_amount)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{inr(r.balance)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${
                      r.status === "Delivered" ? "bg-purple-50 text-purple-700 border-purple-200" :
                      r.status === "Completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      r.status === "In Progress" ? "bg-blue-50 text-blue-700 border-blue-200" :
                      "bg-amber-50 text-amber-700 border-amber-200"
                    }`}>{r.status || "Pending"}</span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button
                        data-testid="whatsapp-btn"
                        onClick={() => sendWhatsApp(r)}
                        className="p-1.5 hover:bg-emerald-50 rounded text-[#25D366]"
                        title="Send receipt via WhatsApp"
                      ><MessageCircle className="h-4 w-4" /></button>
                      <button
                        data-testid="download-pdf-btn"
                        onClick={() => downloadPdf(r)}
                        className="p-1.5 hover:bg-blue-50 rounded text-[#2E77E6]"
                        title="Download PDF"
                      ><FileDown className="h-4 w-4" /></button>
                      {canDeliver && r.status === "Completed" && (
                        <button
                          data-testid="mark-delivered-btn"
                          onClick={() => markDelivered(r)}
                          className="p-1.5 hover:bg-purple-50 rounded text-purple-700"
                          title="Mark as Delivered"
                        ><PackageCheck className="h-4 w-4" /></button>
                      )}
                      <button data-testid="edit-btn" disabled={!canEdit} onClick={() => { if (!canEdit) return; setEditing(r); setOpen(true); }} className={`p-1.5 rounded ${canEdit ? "hover:bg-slate-100 text-[color:var(--brand-teal)]" : "opacity-40 pointer-events-none cursor-not-allowed text-slate-400"}`} title={canEdit ? "Edit" : "Edit disabled — ask admin for Receipt.edit permission"}><Pencil className="h-4 w-4" /></button>
                      <button data-testid="delete-btn" disabled={!canDelete} onClick={() => { if (!canDelete) return; del(r); }} className={`p-1.5 rounded ${canDelete ? "hover:bg-red-50 text-red-600" : "opacity-40 pointer-events-none cursor-not-allowed text-slate-400"}`} title={canDelete ? "Delete" : "Delete disabled — ask admin for Receipt.delete permission"}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <EmptyState label="No records yet" />}
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} setPageSize={setPageSize} testid="receipts-pagination" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Service Details</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <F label="Document No" value={form.doc_no ? formatBill(form.doc_no) : "auto"} disabled />
            <F label="Date" required type="date" value={form.doc_date} onChange={(v) => set("doc_date", v)} testid="rec-date" />
            <F label="Application ID" value={form.application_id} onChange={(v) => set("application_id", v)} />

            <SelectWithAdd
              label="Agent"
              value={form.agent_id}
              onChange={(v) => set("agent_id", v)}
              options={agents.map(a => ({ value: a.id, label: `${a.name}${a.mobile ? " (" + a.mobile + ")" : ""}` }))}
              placeholder="Search by Agent/Mobile"
              onAdd={() => setAddAgentOpen(true)}
              testid="rec-agent"
            />
            <SelectWithAdd
              label="Customer"
              required
              value={form.customer_id}
              onChange={(v) => set("customer_id", v)}
              options={customers.map(c => ({ value: c.id, label: `${c.account}${c.mobile ? " (" + c.mobile + ")" : ""}` }))}
              placeholder="Search by Customer/Mobile"
              onAdd={() => setAddCustomerOpen(true)}
              testid="rec-customer"
            />
            <SelectField label="Service Type" required value={form.service_id} onChange={(v) => { const svc = services.find(s => s.id === v); set("service_id", v); if (svc?.rate && !form.amount) set("amount", svc.rate); }} options={services.map(s => ({ value: s.id, label: s.name }))} placeholder="--- Service Type ---" testid="rec-service" />

            <F label="Amount" required type="number" value={form.amount} onChange={(v) => set("amount", v)} testid="rec-amount" />

            <F label="Balance" type="number" value={form.balance} onChange={(v) => set("balance", v)} disabled />
            <SelectField label="Payment Type" required value={form.payment_type} onChange={(v) => set("payment_type", v)} options={ptypes.map(p => ({ value: p.payment_type, label: p.payment_type }))} placeholder="--- Select Payment Type ---" testid="rec-payment" />
            <F label="Advance" type="number" value={form.advance} onChange={(v) => set("advance", v)} testid="rec-advance" />

            <div className="md:col-span-3">
              <Label className="text-xs font-semibold uppercase tracking-wider">Cash Denominations</Label>
              <div className="mt-1.5 grid grid-cols-2 md:grid-cols-4 gap-3">
                <DenomInput denom="500" value={form.cur_500} onChange={(v) => set("cur_500", v)} testid="rec-cur500" />
                <DenomInput denom="200" value={form.cur_200} onChange={(v) => set("cur_200", v)} testid="rec-cur200" />
                <DenomInput denom="100" value={form.cur_100} onChange={(v) => set("cur_100", v)} testid="rec-cur100" />
                <DenomInput denom="50" value={form.cur_50} onChange={(v) => set("cur_50", v)} testid="rec-cur50" />
              </div>
              <div className="mt-2 text-xs text-slate-500 text-right">
                Cash Total: <span className="font-mono-num font-bold text-slate-800">
                  ₹{(Number(form.cur_500 || 0) * 500 + Number(form.cur_200 || 0) * 200 + Number(form.cur_100 || 0) * 100 + Number(form.cur_50 || 0) * 50).toLocaleString("en-IN")}
                </span>
              </div>
            </div>

            <F label="Remark" value={form.remark_1} onChange={(v) => set("remark_1", v)} />

            <SelectField label="Status" required value={form.status} onChange={(v) => set("status", v)} options={["Pending", "In Progress", "Completed", "Delivered"].map(s => ({ value: s, label: s }))} testid="rec-status" />

            <SelectField label="Login By" required value={form.supervisor} onChange={(v) => set("supervisor", v)} options={users.map(u => ({ value: u.name, label: `${u.name} — ${u.role}` }))} placeholder="--- Select Login By ---" testid="rec-supervisor" />
            <SelectField label="Assigned To" required value={form.operator} onChange={(v) => set("operator", v)} options={users.map(u => ({ value: u.name, label: `${u.name} — ${u.role}` }))} placeholder="--- Select Assigned To ---" testid="rec-operator" />
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Entry Type <span className="text-red-500">*</span></Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="h-10 mt-1.5" data-testid="rec-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="Service">Service</SelectItem>
                  <SelectItem value="Receipt">Receipt</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="md:col-span-3 pt-2 flex gap-2">
              <Button type="submit" className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] text-white" data-testid="rec-save">Save Data</Button>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="bg-slate-500 hover:bg-slate-600 text-white border-0">Close</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <QuickAddDialog
        open={addCustomerOpen}
        setOpen={setAddCustomerOpen}
        title="Add New Customer"
        nameLabel="Customer Name"
        onSave={quickAddCustomer}
      />
      <QuickAddDialog
        open={addAgentOpen}
        setOpen={setAddAgentOpen}
        title="Add New Agent"
        nameLabel="Agent Name"
        onSave={quickAddAgent}
      />
      <CollectBalanceDialog
        open={balanceOpen}
        setOpen={setBalanceOpen}
        ptypes={ptypes}
        onDone={load}
      />
    </div>
  );
}

function SelectWithAdd({ label, value, onChange, options, required, placeholder, onAdd, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider">
        {label}{required && <span className="text-red-500">*</span>}
      </Label>
      <div className="flex gap-1.5">
        <Select value={String(value ?? "")} onValueChange={(v) => onChange(v)}>
          <SelectTrigger className="h-10 flex-1" data-testid={testid}>
            <SelectValue placeholder={placeholder || "Select…"} />
          </SelectTrigger>
          <SelectContent className="bg-white max-h-72">
            {options.map((o) => (<SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={onAdd}
          className="h-10 w-10 rounded-md bg-[#2AA36F] hover:bg-[#238A5D] text-white flex items-center justify-center shrink-0 transition-colors"
          data-testid={`${testid}-add`}
          title={`Add new ${label.toLowerCase()}`}
        >
          <Plus className="h-4 w-4" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, disabled, noLabel, testid }) {
  return (
    <div className="space-y-1.5">
      {!noLabel && (
        <Label className="text-xs font-semibold uppercase tracking-wider">
          {label}{required && <span className="text-red-500">*</span>}
        </Label>
      )}
      <Input
        type={type}
        value={value ?? ""}
        onChange={onChange ? (e) => onChange(type === "number" ? Number(e.target.value) : e.target.value) : undefined}
        disabled={disabled}
        required={required}
        placeholder={noLabel ? label : undefined}
        className={`h-10 ${disabled ? "bg-slate-50 text-slate-500" : ""}`}
        data-testid={testid}
      />
    </div>
  );
}

function DenomInput({ denom, value, onChange, testid }) {
  return (
    <div className="flex items-center h-10 border border-slate-300 rounded-md overflow-hidden bg-white focus-within:ring-2 focus-within:ring-[color:var(--brand-teal)]/40 focus-within:border-[color:var(--brand-teal)]">
      <div className="h-full px-3 flex items-center justify-center font-bold text-sm text-white shrink-0 min-w-[64px]" style={{ background: "var(--brand-navy)" }}>
        <span className="font-mono-num">₹{denom}</span>
      </div>
      <input
        type="number"
        min="0"
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 h-full px-3 outline-none text-right font-mono-num text-slate-800 min-w-0"
        placeholder="0"
        data-testid={testid}
      />
      <div className="h-full px-2 text-[10px] uppercase tracking-wider text-slate-400 border-l border-slate-200 flex items-center bg-slate-50">Qty</div>
    </div>
  );
}


function SelectField({ label, value, onChange, options, required, placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider">
        {label}{required && <span className="text-red-500">*</span>}
      </Label>
      <Select value={String(value ?? "")} onValueChange={(v) => onChange(v)}>
        <SelectTrigger className="h-10" data-testid={testid}><SelectValue placeholder={placeholder || "Select…"} /></SelectTrigger>
        <SelectContent className="bg-white max-h-72">
          {options.map((o) => (<SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );
}


function CollectBalanceDialog({ open, setOpen, ptypes, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [paid, setPaid] = useState(0);
  const [payment, setPayment] = useState("");
  const [remark, setRemark] = useState("");
  const [status, setStatus] = useState("");
  const [collector, setCollector] = useState("");
  const [saving, setSaving] = useState(false);
  const [docDate, setDocDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (open) {
      setQ(""); setResults([]); setSelected(null);
      setPaid(0); setPayment(""); setRemark(""); setStatus(""); setCollector("");
      setDocDate(new Date().toISOString().slice(0, 10));
    }
  }, [open]);

  // When a service is selected, default the status field to that receipt's current status
  useEffect(() => {
    if (selected) {
      setStatus(selected.status || "Pending");
      setCollector(selected.collector_name || "");
    }
  }, [selected]);

  const search = async () => {
    if (!q.trim()) return;
    try {
      const { data } = await api.get("/receipts/search-outstanding", { params: { q } });
      setResults(data);
      if (data.length === 0) toast.info("No outstanding balances match your search");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const outstanding = Number(selected?.balance || 0);
  const balanceLeft = Math.max(0, outstanding - Number(paid || 0));

  const save = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Select an outstanding service first"); return; }
    if (Number(paid) <= 0) { toast.error("Paid amount must be greater than zero"); return; }
    if (Number(paid) > outstanding + 0.01) { toast.error("Paid cannot exceed outstanding"); return; }
    setSaving(true);
    try {
      await api.post("/receipts/balance-payment", {
        parent_doc_id: selected.id,
        paid_amount: Number(paid),
        payment_type: payment,
        doc_date: docDate,
        remark,
      });
      // After collecting balance, if the operator picked a different status, update it too
      if (status && status !== selected.status) {
        // Guard: Delivered requires a collector name if not already stored
        if (status === "Delivered" && !(selected.collector_name || collector).trim()) {
          toast.error("Collector name is required to mark as Delivered");
          setSaving(false);
          return;
        }
        try {
          const payload = { status };
          const c = (collector || "").trim();
          if (c && !selected.collector_name) payload.collector_name = c;
          await api.patch(`/receipts/${selected.id}/status`, payload);
        } catch (err) {
          toast.error(formatErr(err?.response?.data?.detail) || "Status update failed");
        }
      }
      toast.success(`Balance payment of ${paid} recorded`);
      setOpen(false);
      onDone && onDone();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl bg-white" data-testid="collect-balance-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Collect Balance Payment</DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider">Search by Customer Name / Mobile / Doc No</Label>
          <div className="flex gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
              placeholder="e.g. Amit or 9876543210"
              className="h-10 flex-1"
              data-testid="balance-search-input"
              autoFocus
            />
            <Button type="button" onClick={search} className="h-10 bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] text-white" data-testid="balance-search-btn">
              <Search className="h-4 w-4 mr-1.5" /> Search
            </Button>
          </div>
        </div>

        {/* Results dropdown */}
        {results.length > 0 && !selected && (
          <div className="border border-slate-200 rounded-md max-h-56 overflow-y-auto bg-white" data-testid="balance-results">
            {results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => { setSelected(r); setPaid(r.balance); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                data-testid={`balance-result-${r.id}`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-800 text-sm">{r.customer_name} <span className="font-mono-num text-slate-500 text-xs">({r.mobile})</span></div>
                    <div className="text-xs text-slate-500">
                      <span className="font-mono-num">{formatBill(r.doc_no)}</span> • {r.service_name} • {r.doc_date}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Outstanding</div>
                    <div className="font-mono-num font-bold text-red-600">{inr(r.balance)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Selected + Form */}
        {selected && (
          <form onSubmit={save} className="space-y-4 border-t border-slate-200 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Document No</Label>
                <Input value="AUTO" disabled className="h-10 bg-slate-50 font-mono-num text-slate-500" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Date <span className="text-red-500">*</span></Label>
                <Input type="date" required value={docDate} onChange={(e) => setDocDate(e.target.value)} className="h-10" data-testid="balance-date" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Service <span className="text-red-500">*</span></Label>
                <Input
                  value={`${formatBill(selected.doc_no)} — ${selected.customer_name} (${selected.mobile})`}
                  disabled
                  className="h-10 bg-slate-50 font-mono-num text-slate-700"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Outstanding</Label>
                <div className="h-10 flex items-center px-3 bg-slate-100 rounded-md border border-slate-200 font-mono-num font-bold text-red-600" data-testid="balance-outstanding">
                  {inr(outstanding)}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Payment Type <span className="text-red-500">*</span></Label>
                <Select value={payment} onValueChange={setPayment}>
                  <SelectTrigger className="h-10" data-testid="balance-payment-type"><SelectValue placeholder="--- Select Payment Type ---" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {ptypes.map((p) => <SelectItem key={p.id} value={p.payment_type}>{p.payment_type}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Paid <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    required
                    value={paid}
                    onChange={(e) => setPaid(e.target.value)}
                    className="h-10 pr-9 border-emerald-400 ring-emerald-200 focus:ring-emerald-300"
                    data-testid="balance-paid"
                  />
                  {Number(paid) > 0 && Number(paid) <= outstanding && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600">✓</span>
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Balance</Label>
                <div className={`h-10 flex items-center justify-between px-3 rounded-md border font-mono-num font-bold ${
                  balanceLeft === 0 ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                  : "bg-white border-emerald-300 text-slate-800"
                }`} data-testid="balance-remaining">
                  {inr(balanceLeft)}
                  {balanceLeft === 0 && <span className="text-emerald-600">✓</span>}
                </div>
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Remark</Label>
                <Input value={remark} onChange={(e) => setRemark(e.target.value)} className="h-10" data-testid="balance-remark" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">
                  Change Status <span className="text-slate-400 normal-case font-normal">(optional — mark this work as Delivered / Completed after collecting balance)</span>
                </Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-10" data-testid="balance-status"><SelectValue placeholder="Keep current status" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["Pending", "In Progress", "Completed", "Delivered"].map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {status === "Delivered" && (
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">
                    Collector Name <span className="text-red-500">*</span>
                    <span className="text-slate-400 normal-case font-normal ml-1">(person who is collecting the document)</span>
                  </Label>
                  <Input
                    value={collector}
                    onChange={(e) => setCollector(e.target.value)}
                    disabled={!!selected?.collector_name}
                    placeholder={selected?.collector_name ? `Locked: ${selected.collector_name}` : "e.g. Customer / Family member / Agent name"}
                    className={`h-10 ${selected?.collector_name ? "bg-slate-50 text-slate-500" : ""}`}
                    data-testid="balance-collector"
                    required
                  />
                </div>
              )}
            </div>
            <DialogFooter className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setSelected(null)} className="bg-slate-500 hover:bg-slate-600 text-white border-0">Change Service</Button>
              <Button type="submit" disabled={saving} className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] text-white font-semibold" data-testid="balance-save">
                <Save className="h-4 w-4 mr-1.5" /> Save Payment
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
